// Lectura/escritura de datos del producto para el modo Catálogo de productos:
// `productos.medidas` (jsonb), y —lectura— `variantes.atributos` +
// `productos.categoria_producto` que llena la categorización IA. Ver ADR-583 / ADR-808.
//
// OJO CON EL NIVEL: las medidas viven en `productos`, pero lo que el picker y
// las cards manejan son VARIANTES (`MockProductV2.id` es un `variantes.id`).
// Por eso las dos funciones traducen variante → producto padre antes de tocar
// la tabla. Un ropero blanco y uno negro son dos variantes del mismo producto y
// comparten medidas: editar una actualiza las dos, que es justamente lo que
// queremos.

import { supabase } from '../../lib/supabase';
import { paqueteSobreElGuardado } from '../lanzamientos/paqueteDelCatalogo';
import { normalizarMedidas, type Medidas } from '../../lib/medidas';
import { atributosDesdeFicha, fichaDesdeAtributos, type FichaDeProducto } from '../../lib/fichaDeProducto';
import { fetchInChunks } from '../../utils/supabaseChunkedIn';

/** Mapa `variante_id → product_id` para los ids que maneja la UI. */
export async function mapVariantesAProductos(
  varianteIds: string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (varianteIds.length === 0) return out;
  const { data, error } = await supabase
    .from('variantes')
    .select('id, product_id')
    .in('id', varianteIds);
  if (error || !data) return out;
  for (const row of data as { id: string; product_id: string | null }[]) {
    if (row.product_id) out.set(row.id, row.product_id);
  }
  return out;
}

/**
 * Trae las medidas de cada variante seleccionada, indexadas por el id de
 * VARIANTE (que es el que usa la UI), no por el de producto.
 *
 * Devuelve `null` para las que no tienen medidas cargadas — la UI lo muestra
 * como campos vacíos, esperando que el user los complete.
 */
export async function fetchMedidasPorVariante(
  varianteIds: string[],
): Promise<Record<string, Medidas | null>> {
  const result: Record<string, Medidas | null> = {};
  if (varianteIds.length === 0) return result;

  const mapa = await mapVariantesAProductos(varianteIds);
  const productIds = [...new Set(mapa.values())];
  if (productIds.length === 0) return result;

  const { data, error } = await supabase
    .from('productos')
    .select('id, medidas')
    .in('id', productIds);
  if (error || !data) return result;

  const porProducto = new Map<string, Medidas | null>();
  for (const row of data as { id: string; medidas: unknown }[]) {
    porProducto.set(row.id, normalizarMedidas(row.medidas));
  }
  for (const varianteId of varianteIds) {
    const productId = mapa.get(varianteId);
    result[varianteId] = productId
      ? (porProducto.get(productId) ?? null)
      : null;
  }
  return result;
}

/** Categorías cuyo tipo NO se vende por tamaño → la UI oculta las medidas (las que
 *  igual estén cargadas se siguen usando como contexto). El resto —mueble, superficie,
 *  electrodomestico, iluminacion, otro y null (sin clasificar)— admite medidas. */
export const CATEGORIAS_SIN_MEDIDAS = new Set([
  'herramienta',
  'decoracion',
  // Iluminación: un foco no se vende por tamaño y una lámpara tampoco necesita
  // cotas — decisión de Imanol (2026-07-28). Si más adelante hace falta separar
  // foco vs lámpara de pie, se afina la categoría.
  'iluminacion',
]);

export function categoriaAdmiteMedidas(
  categoria: string | null | undefined,
): boolean {
  return !categoria || !CATEGORIAS_SIN_MEDIDAS.has(categoria);
}

/** Palabras de TIPO genérico que no sirven como pill (ya las dice la categoría). El
 *  backend las filtra al extraer, pero los productos categorizados ANTES de ese fix las
 *  tienen guardadas → se filtran también acá, al LEER, para que nunca se muestren. Con y
 *  sin tilde para comparar en lowercase directo. */
const GENERIC_TYPE_WORDS = new Set([
  'mueble',
  'superficie',
  'electrodomestico',
  'electrodoméstico',
  'iluminacion',
  'iluminación',
  'herramienta',
  'decoracion',
  'decoración',
  'otro',
  'producto',
]);

function normalizarAtributos(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    .map((x) => x.trim())
    .filter((x) => !GENERIC_TYPE_WORDS.has(x.toLowerCase()))
    // Tope 4: la infografía dibuja hasta 4 pills (ver muebles.ts / ADR-813).
    .slice(0, 4);
}

/**
 * Lee lo que la categorización IA dejó para cada variante seleccionada:
 *  - `atributos`: las 2-3 características (viven en `variantes.atributos`, por variante).
 *  - `categoria`: el tipo del producto (vive en `productos.categoria_producto`, heredado
 *    del producto padre, como las medidas).
 * Ambos indexados por id de VARIANTE (lo que usa la UI). `[]`/`null` si no hay nada.
 */
export async function fetchAtributosYCategoria(
  varianteIds: string[],
): Promise<{
  atributos: Record<string, string[]>;
  categoria: Record<string, string | null>;
}> {
  const atributos: Record<string, string[]> = {};
  const categoria: Record<string, string | null> = {};
  if (varianteIds.length === 0) return { atributos, categoria };

  // atributos viven en la variante; de paso traemos product_id para la categoría
  const { data: vars, error } = await supabase
    .from('variantes')
    .select('id, product_id, atributos')
    .in('id', varianteIds);
  if (error || !vars) return { atributos, categoria };

  const mapa = new Map<string, string>(); // variante → producto
  for (const row of vars as {
    id: string;
    product_id: string | null;
    atributos: unknown;
  }[]) {
    atributos[row.id] = normalizarAtributos(row.atributos);
    if (row.product_id) mapa.set(row.id, row.product_id);
  }

  const productIds = [...new Set(mapa.values())];
  if (productIds.length === 0) return { atributos, categoria };

  const { data: prods } = await supabase
    .from('productos')
    .select('id, categoria_producto')
    .in('id', productIds);
  const porProducto = new Map<string, string | null>();
  for (const row of (prods ?? []) as {
    id: string;
    categoria_producto: string | null;
  }[]) {
    porProducto.set(row.id, row.categoria_producto ?? null);
  }
  for (const varianteId of varianteIds) {
    const productId = mapa.get(varianteId);
    categoria[varianteId] = productId ? (porProducto.get(productId) ?? null) : null;
  }
  return { atributos, categoria };
}

/**
 * Guarda las medidas del producto padre de esta variante.
 *
 * Sella siempre `origen: 'usuario'` y `actualizado`: si el valor lo tocó una
 * persona, deja de ser un dato deducido por una IA y pasa a ser fuente de
 * verdad (ADR-583). El caller debe llamar con debounce — se dispara por cada
 * tecla si no.
 */
export async function saveMedidasDeVariante(
  varianteId: string,
  medidas: Medidas,
): Promise<{ ok: boolean; error?: string }> {
  const mapa = await mapVariantesAProductos([varianteId]);
  const productId = mapa.get(varianteId);
  if (!productId) {
    return { ok: false, error: 'No se encontró el producto de esta variante.' };
  }

  const payload: Medidas = {
    ...medidas,
    origen: 'usuario',
    actualizado: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('productos')
    .update({ medidas: payload })
    .eq('id', productId);

  if (error) {
    console.error('[medidasApi] no se pudo guardar', error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Persiste los atributos CURADOS por el cliente en `variantes.atributos` (jsonb). Los
 * atributos viven en la variante (no en el producto padre, a diferencia de las medidas),
 * así que se escribe directo por varianteId. Antes se precargaban de la IA pero las
 * ediciones no se guardaban; con esto quedan para la próxima sesión. Llamar con debounce.
 */
export async function saveAtributosDeVariante(
  varianteId: string,
  atributos: string[],
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('variantes')
    .update({ atributos })
    .eq('id', varianteId);
  if (error) {
    console.error('[medidasApi] no se pudieron guardar atributos', error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Las características de cada variante, desde `variantes.atributos` (lo que dejó la IA al subir
 * el producto y lo que se cargó después), como ficha. Sin tope: el de 4 de `normalizarAtributos`
 * es de las pills del modo Infografía (ADR-2026-10-14-las-caracteristicas-viven-en-atributos).
 * Con error, fichas vacías: la pantalla sigue.
 */
export async function fetchFichaPorVariante(
  varianteIds: string[],
): Promise<Record<string, FichaDeProducto>> {
  const result: Record<string, FichaDeProducto> = {};
  for (const v of varianteIds) result[v] = { campos: [] };
  if (varianteIds.length === 0) return result;
  const filas = await fetchInChunks<{ id: string; atributos: unknown }>(varianteIds, (chunk) =>
    supabase.from('variantes').select('id, atributos').in('id', chunk),
  ).catch(() => []);
  for (const f of filas) result[f.id] = fichaDesdeAtributos(f.atributos);
  return result;
}

/** Guarda las características de la variante en `variantes.atributos`, como líneas "Clave: valor". */
export async function saveFichaDeVariante(
  varianteId: string,
  ficha: FichaDeProducto,
): Promise<{ ok: boolean; error?: string }> {
  return saveAtributosDeVariante(varianteId, atributosDesdeFicha(ficha));
}

/** El paquete para el envío de un producto: peso (kg) y medidas (cm). Ver `paqueteDelCatalogo.ts`. */
export interface PaqueteDeEnvio {
  alto?: number | null;
  ancho?: number | null;
  profundidad?: number | null;
  peso_kg?: number | null;
}

/**
 * Suma el paquete a `productos.medidas_envio` del producto de la variante, sin borrar lo que ya
 * tenía (cómo se entrega, por ejemplo). La columna es de la migración 20261018120000: sin ella
 * devuelve `sinColumna` en vez de fallar (ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf).
 */
export async function savePaqueteDeVariante(
  varianteId: string,
  paquete: PaqueteDeEnvio,
  /**
   * `origen`: `usuario` si la persona lo revisó (Subir productos), `ia` si lo escribió el agente.
   * Un paquete con origen `usuario` no se pisa sin `sobrescribir` (vuelve `confirmado`), como las
   * medidas (ADR-2026-10-19-revision-greptile-pdf-y-catalogo). Antes todo se marcaba `usuario`.
   */
  opts: { sobrescribir?: boolean; origen?: 'usuario' | 'ia' } = {},
): Promise<{ ok: boolean; sinColumna?: boolean; confirmado?: boolean; error?: string }> {
  const mapa = await mapVariantesAProductos([varianteId]);
  const productId = mapa.get(varianteId);
  if (!productId) return { ok: false, error: 'No se encontró el producto de esta variante.' };
  const { data, error: eLeer } = await supabase.from('productos').select('medidas_envio').eq('id', productId).maybeSingle();
  if (eLeer) {
    if (eLeer.code === '42703') return { ok: false, sinColumna: true };
    return { ok: false, error: eLeer.message };
  }
  const actual = ((data as { medidas_envio?: unknown } | null)?.medidas_envio ?? {}) as Record<string, unknown>;
  const nuevo = paqueteSobreElGuardado(actual, paquete, opts);
  if ('confirmado' in nuevo) return { ok: false, confirmado: true };
  const { error } = await supabase
    .from('productos')
    .update({ medidas_envio: nuevo.medidas_envio })
    .eq('id', productId);
  if (error) {
    console.error('[medidasApi] no se pudo guardar el paquete', error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}
